import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    createParkingGuestReasonFormSchema,
    type CreateParkingGuestReasonFormSchemaValues,
    useCreateParkingGuestReason,
} from 'src/features/parkingManagement/api/ReasonsForGuest/useCreateParkingGuestReason';
import { GUEST_REASONS_RESOURCE } from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface NewReasonForGuestProps extends DataTableNewEntryProps<any> {}

const NewReasonForGuest = ({ className }: NewReasonForGuestProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { toast } = useToast();

    const form = useForm<CreateParkingGuestReasonFormSchemaValues>({
        resolver: zodResolver(createParkingGuestReasonFormSchema),
        defaultValues: {
            name: undefined,
        },
    });

    const { mutate, isPending } = useCreateParkingGuestReason({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [GUEST_REASONS_RESOURCE],
            });
            setIsModalOpen(false);
            form.reset();
            toast({
                title: formatMessage(parkingManagementMessages.newReasonCreatedSuccess),
                description: formatMessage(parkingManagementMessages.newReasonCreatedSuccessDescription),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            const errorMessage = error
                ? `${error[0]}, ${error[1][0]}`
                : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const onSubmit: SubmitHandler<CreateParkingGuestReasonFormSchemaValues> = (values) => {
        if (!parkingId) return;
        const submitValues = {
            parkingId: parkingId,
            ...values,
        };
        mutate(submitValues);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>
                <Button variant="outline" className={cn('ml-auto cursor-pointer', className)}>
                    <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                    {formatMessage(tableMessages.new, {
                        entry: formatMessage(parkingManagementMessages.reason).toLowerCase(),
                    })}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.ADD} />
                        <div>
                            <DialogTitle>
                                {formatMessage(tableMessages.new, {
                                    entry: formatMessage(parkingManagementMessages.reason).toLowerCase(),
                                })}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(parkingManagementMessages.addNewReasonDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(generalMessages.name)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Input
                                                type="text"
                                                placeholder={formatMessage(generalMessages.name)}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(parkingManagementMessages.addNewReason)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default NewReasonForGuest;
